/**
 * The questions a resumed run has already been through.
 *
 * A run does not always start at the first question: the host page can hand the
 * wizard the answers it already collected, and re-entering the wizard stage
 * remounts it with everything answered so far. Those questions were passed, but
 * nothing in the component's own history says so — it starts empty.
 *
 * That gap is the same one `passedQuestionIds` exists to close, only one run
 * earlier. If a resumed answer's question is hidden in the first response it is
 * absent from the visible list, so the next answer rebuilds the actions without
 * it and the server shows it again as unanswered — and judged by the visible
 * list alone the wizard navigates backwards onto a question the user has
 * already answered.
 *
 * An action names its question by `questionId`, which is the identity the step
 * resolver keys on.
 */
export function passedQuestionIdsFromActions(actions) {
    return new Set(actions.map(action => action.questionId));
}
/**
 * The questions behind the user once the one at `currentQuestionIndex` is
 * answered — that question included.
 *
 * A forward side-navigation jump skips over questions without answering them,
 * and this rule counts them as behind the user all the same; otherwise
 * answering the jump target would send the wizard straight back to the first
 * one skipped, which is what the jump said to leave.
 *
 * It is exported because the set has to be *recorded*, not merely computed:
 * persisting only the answered question leaves the skipped ones resting on the
 * current list, and once one of them disappears and comes back the list can no
 * longer supply it. The step would then reopen a question an earlier step had
 * already treated as passed. Both the resolver and the caller that fills the
 * durable set read this one rule, so the two cannot drift apart.
 */
export function passedQuestionIdsThrough(questions, currentQuestionIndex) {
    return questions
        .slice(0, Math.max(currentQuestionIndex + 1, 0))
        .map(question => question.questionId);
}
/**
 * The questions a submitted answer leaves behind it.
 *
 * Without a jump that is simply the prefix through the answered question. With
 * one, the jump is itself a statement about everything between here and the
 * target: the user chose to move past it. Those questions have to be recorded
 * now, because the pre-answer list is the last place they are guaranteed to
 * appear — the answer being submitted can hide them, and once hidden neither
 * the list nor the durable set could name them, so a later reappearance would
 * reopen a question the jump had already left behind.
 *
 * The target itself is excluded: the user is moving *to* it, and it stays
 * pending until they answer it. A backwards jump and a target that is not in
 * the list both leave nothing extra behind.
 */
export function passedQuestionIdsOnAnswer({ questions, currentQuestionIndex, nextQuestionId, }) {
    const throughCurrent = passedQuestionIdsThrough(questions, currentQuestionIndex);
    if (!nextQuestionId) {
        return throughCurrent;
    }
    const targetIndex = questions.findIndex(question => question.questionId === nextQuestionId);
    return targetIndex > currentQuestionIndex
        ? passedQuestionIdsThrough(questions, targetIndex - 1)
        : throughCurrent;
}
/**
 * Picks the next question by identity rather than by position.
 *
 * The list is not stable across an answer: a question is visible only while its
 * ask-if condition holds, so answering one can add questions and can remove
 * others — including the one just answered. An index taken from the list before
 * the answer therefore does not address the same question afterwards. Comparing
 * it against the new list either completes the wizard while questions are still
 * waiting, or walks to whichever question happens to sit at that offset now.
 *
 * So the decision is made over identities: everything up to and including the
 * question just answered is behind us, and the next step is the first question
 * in the new list that is not. That covers the list growing, shrinking, and the
 * current question vanishing, and it completes only when the new list holds
 * nothing the user has not already passed.
 *
 * `passedQuestionIds` carries that knowledge across refetches, and it is needed
 * because the pre-answer list alone forgets. `getUpdatedQuestionsWithAnswer`
 * rebuilds the actions from the *visible* questions, so answering a later
 * question can drop the action of one that has since been hidden — and without
 * that action the server may show the hidden question again. Judged by the
 * previous list alone it would then look unanswered, and the wizard would
 * navigate backwards onto a question the user has already been through.
 */
export function resolveStepAfterAnswer({ previousQuestions, freshQuestions, currentQuestionIndex, passedQuestionIds, }) {
    const passed = new Set([
        ...(passedQuestionIds ?? []),
        ...passedQuestionIdsThrough(previousQuestions, currentQuestionIndex),
    ]);
    const nextIndex = freshQuestions.findIndex(question => !passed.has(question.questionId));
    return nextIndex === -1
        ? { type: "complete" }
        : { type: "goto", index: nextIndex };
}
/**
 * Where the wizard goes when a jump target was requested (side navigation).
 *
 * A hit is unambiguous: go there. A miss is not — the answer submitted along
 * with the jump can hide the target. Falling back to the first question would
 * reopen something the user has already passed, so the miss is resolved by the
 * same identity rule as an ordinary answer.
 */
export function resolveStepForJump({ nextQuestionId, previousQuestions, freshQuestions, currentQuestionIndex, passedQuestionIds, }) {
    const index = freshQuestions.findIndex(question => question.questionId === nextQuestionId);
    if (index !== -1) {
        return { type: "goto", index };
    }
    return resolveStepAfterAnswer({
        previousQuestions,
        freshQuestions,
        currentQuestionIndex,
        passedQuestionIds,
    });
}
/**
 * Where the wizard starts when a run resumes from answers already given.
 *
 * The resumed answers name the question the user last worked on, and that is
 * where the run picks up — but the question can be gone: the visibility filter
 * decides against the *current* document, so an answer given earlier can name a
 * question whose ask-if condition no longer holds.
 *
 * A miss is then resolved by the same identity rule the other two step
 * resolvers use, rather than by falling back to the first question. Those same
 * resumed answers say the earlier questions are behind the user, so restarting
 * at the top would reopen one of them — the very thing `passedQuestionIds`
 * records to prevent.
 */
export function resolveStepOnResume({ lastAnsweredQuestionId, freshQuestions, passedQuestionIds, }) {
    const resumeIndex = lastAnsweredQuestionId
        ? freshQuestions.findIndex(question => question.questionId === lastAnsweredQuestionId)
        : -1;
    if (resumeIndex !== -1) {
        return { type: "goto", index: resumeIndex };
    }
    // No question has been passed *within* this run yet, so the pre-answer list
    // is empty and there is no current index — the seeded set carries everything
    // that is known to be behind the user.
    return resolveStepAfterAnswer({
        previousQuestions: [],
        freshQuestions,
        currentQuestionIndex: -1,
        passedQuestionIds,
    });
}
//# sourceMappingURL=questionSteps.js.map